import { MapsWidget } from '@/components/MapsWidget';
import { BuscaPassagensWidget } from '@/components/BuscaPassagensWidget';
import { PromptAlignedFAQ } from '@/components/PromptAlignedFAQ';

export const frontmatter = {
  category: 'guia',
  title: 'Distância Curitiba–Ilha do Mel: Rota, Tempo e Melhores Opções',
  excerpt:
    'De Curitiba à Ilha do Mel são cerca de 120 km de carro até Pontal do Sul (1h30), mais 30 min de barco. Veja rotas, tempos reais, pedágios, opções de ônibus e como planejar a viagem.',
  publishedAt: '2026-05-17',
  author: 'Guia Ilha do Mel',
  tags: ['curitiba', 'distancia', 'como-chegar', 'rota', 'onibus'],
  coverImage: '/images/google/ilha-do-mel-2.webp',
  readingTime: '7 min',
};

# Distância de Curitiba para [Ilha do Mel](/): Rota, Tempo e Melhores Opções

A **distância de Curitiba para a Ilha do Mel** é de aproximadamente **120 km por terra** até o ponto de embarque em Pontal do Sul — mais 30 minutos de travessia de barco. O tempo total da viagem, saindo do centro de Curitiba e chegando no trapiche da ilha, fica entre **2h30 e 3h**, dependendo do trânsito, da fila no embarque e da opção de transporte escolhida.

Aqui está o mapa do percurso completo:

<MapsWidget query="Curitiba, PR para Pontal do Sul, Pontal do Paraná, PR" />

---

## Distâncias e Tempos: Resumo Rápido

| Trecho                                     | Distância               | Tempo estimado                            |
| ------------------------------------------ | ----------------------- | ----------------------------------------- |
| Curitiba → Pontal do Sul (carro, BR-277)   | ~120 km                 | 1h30 a 1h45                               |
| Curitiba → Paranaguá (carro, BR-277)       | ~90 km                  | 1h15 a 1h30                               |
| Paranaguá → Pontal do Sul (carro, litoral) | ~30 km                  | 30 min                                    |
| Pontal do Sul → Ilha do Mel (barco/barca)  | ~9 km náuticos          | 30–35 min (barca) / 15 min (táxi náutico) |
| **Total Curitiba → Ilha do Mel**           | **~120 km + travessia** | **2h30 a 3h**                             |

> **Atenção:** esses tempos não incluem filas no embarque, que podem ser de 30 min a 1h na alta temporada (dezembro a fevereiro e feriados).

---

## Rota de Carro: Curitiba até Pontal do Sul

A rota mais rápida de carro é totalmente por rodovias federais e estaduais bem sinalizadas:

1. **Saia de Curitiba pela BR-277 sentido leste** (Rodovia do Café / Estrada da Ribeira)
2. **Percorra ~70 km** pela BR-277 até o entroncamento de Paranaguá
3. **Continue pela PR-407** (Estrada do Mel) sentido Pontal do Sul por ~30 km
4. **Chegue ao Terminal de Embarque** na Rua Alameda do Café, Balneário Pontal do Sul

### Pedágios na Rota Curitiba → Pontal do Sul

A BR-277 tem praças de pedágio. Considerando saída da capital, você passa por:

| Praça de Pedágio | Localização aproximada | Valor estimado (2026) |
| ---------------- | ---------------------- | --------------------- |
| Contorno Leste   | Saída de Curitiba      | R$ 6–8                |
| Paranaguá        | Próximo a Paranaguá    | R$ 8–12               |

> Os valores são aproximados e variam conforme a categoria do veículo e a concessionária em vigor. Consulte sempre o site da ANTT antes de viajar.

---

## Rota Alternativa: Curitiba via Paranaguá

Se você quiser **conhecer Paranaguá** no caminho, pode fazer a rota:

1. **Curitiba → Paranaguá** pela BR-277 (~90 km, 1h15)
2. **Passeio pelo centro histórico de Paranaguá** (opcional)
3. **Paranaguá → Porto de Paranaguá**, onde há barca para a ilha
4. **Travessia Paranaguá → Ilha do Mel**: 1h45 (mais longa, mas mais cênica)

Essa rota é recomendada para quem quer dividir a viagem com uma visita ao centro histórico de Paranaguá ou ao Mercado do Café.

---

## De Ônibus: Curitiba até a Ilha do Mel

É possível chegar à ilha totalmente sem carro. O trajeto de ônibus tem duas etapas:

### Etapa 1: Curitiba → Paranaguá ou Pontal do Sul

**Terminal Rodoviário de Curitiba (Rodoferroviária):**

- **Destino Paranaguá**: várias saídas por dia (empresas Viação Graciosa e São José). Duração: ~1h30. Preço: ~R$ 30–40.
- **Destino Pontal do Paraná** (menos opções): verificar na rodoviária. Duração: ~2h.

<BuscaPassagensWidget origem="Curitiba" destino="Paranaguá" />

### Etapa 2: Paranaguá ou Pontal do Sul → Ilha do Mel (barco)

De Paranaguá:

- Saídas às 15h30 (seg–sex) e 9h30 (sáb–dom)
- Travessia: 1h45
- Preço: ~R$ 76,50 ida e volta (2025)

De Pontal do Sul:

- Saídas das 8h às 18h, a cada 30 min (alta temporada) ou a cada 1h (baixa)
- Travessia: 30–35 min
- Preço: ~R$ 44,18 ida e volta (2025)

> **Recomendação**: se for de ônibus, vá até Paranaguá e pegue o barco de lá. A travessia é mais longa, mas inclui uma passagem pela Baía de Paranaguá que é belíssima — e você pode aproveitar para conhecer o centro histórico enquanto espera.

---

## Tempo Total por Modal de Transporte

| Modal                            | Curitiba → Embarque   | Travessia | Total                      |
| -------------------------------- | --------------------- | --------- | -------------------------- |
| Carro (via Pontal do Sul)        | 1h30–1h45             | 30–35 min | **2h15 a 2h30**            |
| Carro (via Paranaguá)            | 1h15–1h30             | 1h45      | **3h a 3h15**              |
| Ônibus + barco (Pontal)          | ~2h                   | 30–35 min | **~2h45**                  |
| Ônibus + barco (Paranaguá)       | ~1h30                 | 1h45      | **~3h30**                  |
| Trem Serra Verde Express + barco | 4h (trem) + 30–35 min | 30–35 min | **~5h (mas inesquecível)** |

---

## Estacionamento em Pontal do Sul

Se for de carro, você não leva o veículo para a ilha — ele fica em Pontal do Sul durante toda a viagem:

- **Estacionamentos pagos** próximos ao terminal: R$ 25–40/diária
- **Serviço de leva e traz**: alguns estacionamentos buscam o turista no hotel em Curitiba e levam até o embarque (verificar com o estacionamento com antecedência)
- **Dica**: reserve um estacionamento coberto em alta temporada — muitos lotam na sexta à noite de feriado

---

## Vale a Pena a Viagem de Curitiba?

Absolutamente. Em menos de 3 horas a partir do centro de Curitiba você está em uma ilha sem carros, sem asfalto e com 94% de área preservada. Nenhum outro destino do Sul entrega essa combinação de acessibilidade e natureza selvagem em tão pouco tempo.

A ilha limita a entrada a **5.000 visitantes por dia** — o que significa que, mesmo nos feriados mais movimentados, a experiência é bem diferente das praias urbanas do litoral catarinense.

Confira o roteiro completo e tudo o que fazer na chegada no Guia Completo da [Ilha do Mel](/).

---

<PromptAlignedFAQ
  faqs={[
    {
      q: 'Qual a distância de Curitiba para a Ilha do Mel?',
      a: 'A distância de carro de Curitiba até Pontal do Sul (ponto de embarque) é de aproximadamente 120 km, percorridos em 1h30 a 1h45 pela BR-277. Somando a travessia de barco (30–35 min), o tempo total fica entre 2h15 e 2h30.',
    },
    {
      q: 'Qual a rota mais rápida de Curitiba para a Ilha do Mel?',
      a: 'A rota mais rápida é pela BR-277 até Paranaguá e depois pela PR-407 até Pontal do Sul, onde se embarca na barca ou táxi náutico. O táxi náutico (15 min de travessia) é mais rápido que a barca (30–35 min), mas custa mais.',
    },
    {
      q: 'Dá para ir de Curitiba à Ilha do Mel sem carro?',
      a: 'Sim. Há ônibus de Curitiba para Paranaguá com saídas frequentes (empresas Graciosa e São José). De Paranaguá, você pega a barca para a ilha. O trajeto total fica em torno de 3h30. Outra opção é contratar um tour organizado saindo de Curitiba.',
    },
    {
      q: 'Quanto tempo leva de Curitiba para a Ilha do Mel de carro?',
      a: 'De carro, são 1h30 a 1h45 de Curitiba até Pontal do Sul, mais 30–35 minutos de barco. O tempo total fica entre 2h15 e 2h30 saindo do centro de Curitiba. Na alta temporada, some 30–60 minutos para filas no embarque.',
    },
    {
      q: 'Tem pedágio na estrada de Curitiba para a Ilha do Mel?',
      a: 'Sim, a BR-277 tem praças de pedágio. Saindo de Curitiba em direção a Pontal do Sul, você passa por pelo menos duas praças, com valores que variam de R$ 6 a R$ 12 por praça conforme a categoria do veículo (valores de 2026). Consulte o site da ANTT para valores atualizados.',
    },
  ]}
/>
